import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { client, getAuthToken, removeAuthToken, removeUserData } from '../lib/client';
import { useProfile, type User } from '../hooks/queries/useMe';
import { formatPhoneNumber } from '../utils/formatters';

const DeleteInfo: React.FC = () => {
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const isLoggedIn = Boolean(getAuthToken());
  const { data: user, isLoading } = useProfile(isLoggedIn);

  const userWithApple = user as (User & { appleId?: string; apple_id?: string }) | undefined;
  const name = user?.name ?? (isLoading ? '불러오는 중...' : '-');
  const rawPhone = user?.phone_number ?? '';
  const phone = rawPhone ? formatPhoneNumber(rawPhone) : '-';
  const appleId = userWithApple?.appleId ?? userWithApple?.apple_id ?? null;
  const canWithdraw = Boolean(user?.id) && hasConfirmed;

  const handleWithdrawClick = async () => {
    if (!isLoggedIn) {
      toast.error('로그인이 필요합니다.', { duration: 2500, style: { textAlign: 'center' } });
      return;
    }

    if (!canWithdraw || isSubmitting) return;

    const ok = window.confirm(
      '정말 탈퇴하시겠어요?\n\n탈퇴 시 개인정보가 즉시 파기되며, 기존 참여 그룹에서 모두 탈퇴 처리됩니다.'
    );
    if (!ok) return;

    setIsSubmitting(true);
    try {
      await client.delete('/auth/withdraw');

      // 로컬 세션 정리
      removeAuthToken();
      removeUserData();
      localStorage.clear();

      toast.success('탈퇴가 완료되었습니다.', { duration: 2500, style: { textAlign: 'center' } });
      navigate('/', { replace: true });
    } catch (e: any) {
      const message = e?.response?.data?.error ?? '탈퇴 처리 중 오류가 발생했습니다.';
      toast.error(message, { duration: 3000, style: { textAlign: 'center' } });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-2xl px-6 py-8 shadow-sm border border-gray-100">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">탈퇴하기</h1>
          <p className="text-gray-600 text-sm">
            아래 정보가 맞는지 확인하고, 안내사항을 읽은 뒤 진행해주세요.
          </p>
        </div>

        {/* 내 정보 */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">이름</span>
            <span className="text-sm font-semibold text-gray-900">{name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">휴대전화 번호</span>
            <span className="text-sm font-semibold text-gray-900">{phone}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Apple ID</span>
            <span className="text-sm font-semibold text-gray-900">
              {appleId ? appleId : '연동되지 않음'}
            </span>
          </div>
        </div>

        {!isLoggedIn && (
          <div className="mt-4 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
            로그인이 필요합니다.{' '}
            <Link to="/auth" className="font-semibold underline underline-offset-2">
              로그인 하러가기
            </Link>
          </div>
        )}

        {/* 강한 경고 */}
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.59c.75 1.334-.214 3.01-1.742 3.01H3.48c-1.528 0-2.492-1.676-1.742-3.01l6.52-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-red-900">
              <p className="font-bold mb-1">탈퇴 전 꼭 확인해주세요</p>
              <ul className="list-disc pl-5 space-y-1 text-red-800">
                <li>탈퇴 시 고객님의 개인정보는 <span className="font-semibold">즉시 파기</span>됩니다.</li>
                <li>기존에 참여한 그룹은 모두 <span className="font-semibold">탈퇴</span>됩니다.</li>
                <li>서비스를 다시 이용하기 위해서는 다시 <span className="font-semibold">가입</span>해야 합니다.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 동의 체크 */}
        <div className="mt-6 flex flex-row items-center gap-2 mx-2 cursor-pointer select-none"
        onClick={() => setHasConfirmed(!hasConfirmed)}
        >
          <input
            type="checkbox"
            checked={hasConfirmed}
            onChange={(e) => setHasConfirmed(e.target.checked)}
            className="h-3 w-3  rounded border-gray-300 text-red-600 focus:ring-red-500"
            disabled={!user?.id}
          />
          <span className=" text-sm text-gray-700">
            위 경고사항을 모두 읽었습니다.
          </span>
        </div>

        {/* 탈퇴 버튼 */}
        <button
          type="button"
          onClick={handleWithdrawClick}
          disabled={!canWithdraw || isSubmitting}
          className={[
            'mt-6 w-full font-bold py-4 px-4 rounded-xl transition-colors duration-200 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
            canWithdraw && !isSubmitting
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed',
          ].join(' ')}
        >
          {isSubmitting ? '처리 중...' : '탈퇴하기'}
        </button>
      </div>
    </div>
  );
};

export default DeleteInfo;